import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../interface_user/header/header";
import Footer from "../interface_user/footer/footer";
import Footer1 from "../interface_user/footer/footer1";
import { jwtDecode } from 'jwt-decode';
import { baseApiUrl } from "../../services/api";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Timeline,
  Avatar,
  Badge,
  Empty,
  Spin,
  Button,
  Table,
  Tag,
  Tooltip,
  Modal,
  Tabs
} from "antd";
import {
  ProjectOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  EyeOutlined,
  HeartOutlined,
  CalendarOutlined,
  PlusOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  LikeOutlined,
  DislikeOutlined,
  CommentOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";

const { TabPane } = Tabs;

function UserDashboard() {
  const [currentUser, setCurrentUser] = useState(null);
  const [projets, setProjets] = useState([]);
  const [publications, setPublications] = useState([]);
  const [statistiques, setStatistiques] = useState({});
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [likesData, setLikesData] = useState({});
  const [commentsData, setCommentsData] = useState({});

  // Récupérer les données utilisateur
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUser(decoded);
        console.log("Utilisateur connecté:", decoded);
      } catch (error) {
        console.error("Erreur lors du décodage du token:", error);
      }
    }
  }, []);

  // Récupérer les projets de l'utilisateur
  const fetchProjets = async () => {
    if (!currentUser?.id) return;

    try {
      const response = await baseApiUrl.get("/projet/liste");
      const allProjets = Array.isArray(response.data) ? response.data : [];
      const userProjets = allProjets.filter(
        (projet) => projet.utilisateur?.id === currentUser.id
      );
      setProjets(userProjets);

      // Récupérer les données de likes et commentaires pour chaque projet
      userProjets.forEach(projet => {
        fetchLikeCounts(projet.id, 'projet');
        fetchCommentsCounts(projet.id, 'projet');
      });
    } catch (error) {
      console.error("Erreur lors de la récupération des projets:", error);
    }
  };

  // Récupérer les publications de l'utilisateur
  const fetchPublications = async () => {
    if (!currentUser?.id) return;

    try {
      const response = await baseApiUrl.get("/publication/liste");
      const allPublications = Array.isArray(response.data) ? response.data : [];
      const userPublications = allPublications.filter(
        (pub) => pub.utilisateur?.id === currentUser.id
      );
      setPublications(userPublications);

      // Récupérer les données de likes et commentaires pour chaque publication
      userPublications.forEach(pub => {
        fetchLikeCounts(pub.id, 'publication');
        fetchCommentsCounts(pub.id, 'publication');
      });
    } catch (error) {
      console.error("Erreur lors de la récupération des publications:", error);
    }
  };

  // Récupérer les compteurs de likes
  const fetchLikeCounts = async (entityId, entityType) => {
    try {
      const response = await fetch(`${baseApiUrl.defaults.baseURL}/likes/${entityType}/${entityId}`);
      if (response.ok) {
        const data = await response.json();
        setLikesData(prev => ({
          ...prev,
          [`${entityType}_${entityId}`]: {
            likeCount: data.likeCount || 0,
            dislikeCount: data.dislikeCount || 0
          }
        }));
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des compteurs :", error);
    }
  };

  // Récupérer le nombre de commentaires
  const fetchCommentsCounts = async (entityId, entityType) => {
    try {
      const response = await fetch(
        `${baseApiUrl.defaults.baseURL}/commentaire/${entityType}/${entityId}/commentaires`
      );
      if (response.ok) {
        const data = await response.json();
        setCommentsData(prev => ({
          ...prev,
          [`${entityType}_${entityId}`]: Array.isArray(data) ? data.length : 0
        }));
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des commentaires:", error);
    }
  };

  // Calculer les statistiques
  const calculerStatistiques = () => {
    const projetsValides = projets.filter(p => p.actif === true);
    const projetsEnAttente = projets.filter(p => p.actif === false && !p.rejete);
    const projetsRejetes = projets.filter(p => p.rejete === true);

    const publicationsValides = publications.filter(p => p.actif === true);
    const publicationsEnAttente = publications.filter(p => p.actif === false && !p.rejete);
    const publicationsRejetees = publications.filter(p => p.rejete === true);

    const totalProjets = projets.length;
    const totalPublications = publications.length;
    const totalElements = totalProjets + totalPublications;
    const elementsValides = projetsValides.length + publicationsValides.length;
    const elementsRejetes = projetsRejetes.length + publicationsRejetees.length;
    const tauxValidation = totalElements > 0 ? Math.round((elementsValides / totalElements) * 100) : 0;

    setStatistiques({
      totalProjets,
      totalPublications,
      projetsValides: projetsValides.length,
      projetsEnAttente: projetsEnAttente.length,
      projetsRejetes: projetsRejetes.length,
      publicationsValides: publicationsValides.length,
      publicationsEnAttente: publicationsEnAttente.length,
      publicationsRejetees: publicationsRejetees.length,
      tauxValidation,
      totalElements,
      elementsValides,
      elementsRejetes
    });
  };

  // Générer les activités récentes
  const genererActivitesRecentes = () => {
    const activites = [];

    // Ajouter les projets récents
    projets.slice(0, 3).forEach(projet => {
      activites.push({
        type: 'projet',
        titre: projet.titre,
        date: projet.created || projet.dateCreation,
        statut: projet.actif,
        rejete: projet.rejete,
        id: projet.id
      });
    });

    // Ajouter les publications récentes
    publications.slice(0, 3).forEach(pub => {
      activites.push({
        type: 'publication',
        titre: pub.titre,
        date: pub.created || pub.dateCreation,
        statut: pub.actif,
        rejete: pub.rejete,
        id: pub.id
      });
    });

    // Trier par date (plus récent en premier)
    activites.sort((a, b) => new Date(b.date) - new Date(a.date));

    setRecentActivities(activites.slice(0, 6));
  };

  // Charger toutes les données
  useEffect(() => {
    if (currentUser) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchProjets(), fetchPublications()]);
        setLoading(false);
      };
      loadData();
    }
  }, [currentUser]);

  // Calculer les statistiques quand les données changent
  useEffect(() => {
    if (projets.length >= 0 && publications.length >= 0) {
      calculerStatistiques();
      genererActivitesRecentes();
    }
  }, [projets, publications]);

  const formatDate = (dateString) => {
    if (!dateString) return "Date non disponible";
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return "Date invalide";
    }
  };

  const getStatutTag = (item) => {
    if (item.rejete) {
      return <Tag color="red">Rejeté</Tag>;
    } else if (item.actif) {
      return <Tag color="green">Validé</Tag>;
    } else {
      return <Tag color="orange">En attente</Tag>;
    }
  };

  const showRejectModal = (item, type) => {
    Modal.info({
      title: 'Motif de rejet',
      content: (
        <div>
          <p><strong>{type === 'projet' ? 'Projet' : 'Publication'}:</strong> {item.titre}</p>
          <p><strong>Motif de rejet:</strong> {item.motifRejet || 'Aucun motif spécifié'}</p>
          <p><strong>Date de rejet:</strong> {item.dateRejet ? formatDate(item.dateRejet) : 'Non spécifiée'}</p>
        </div>
      ),
      width: 500,
    });
  };

  // Colonnes pour le tableau des projets
  const projetColumns = [
    {
      title: 'Titre',
      dataIndex: 'titre',
      key: 'titre',
      render: (text, record) => (
        <Link to={`/projet/${record.id}`} className="text-decoration-none">
          <strong>{text}</strong>
        </Link>
      ),
    },
    {
      title: 'Date de création',
      dataIndex: 'created',
      key: 'created',
      render: (date) => formatDate(date),
    },
    {
      title: 'Statut',
      dataIndex: 'actif',
      key: 'actif',
      render: (_, record) => getStatutTag(record),
    },
    {
      title: 'Interactions',
      key: 'interactions',
      render: (_, record) => {
        const likes = likesData[`projet_${record.id}`];
        const comments = commentsData[`projet_${record.id}`] || 0;
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ color: '#52c41a' }}>
              <LikeOutlined /> {likes?.likeCount || 0}
            </span>
            <span style={{ color: '#ff4d4f' }}>
              <DislikeOutlined /> {likes?.dislikeCount || 0}
            </span>
            <span style={{ color: '#1890ff' }}>
              <CommentOutlined /> {comments}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Tooltip title="Voir le projet">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => window.open(`/projet/${record.id}`, '_blank')}
            />
          </Tooltip>
          {!record.actif && !record.rejete && (
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => window.open(`/projet/modifier/${record.id}`, '_blank')}
              />
            </Tooltip>
          )}
          {record.rejete && (
            <Tooltip title="Voir motif de rejet">
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                style={{ color: '#ff4d4f' }}
                onClick={() => showRejectModal(record, 'projet')}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  // Colonnes pour le tableau des publications
  const publicationColumns = [
    {
      title: 'Titre',
      dataIndex: 'titre',
      key: 'titre',
      render: (text, record) => (
        <Link to={`/publication/${record.id}`} className="text-decoration-none">
          <strong>{text}</strong>
        </Link>
      ),
    },
    {
      title: 'Date de création',
      dataIndex: 'created',
      key: 'created',
      render: (date) => formatDate(date),
    },
    {
      title: 'Statut',
      dataIndex: 'actif',
      key: 'actif',
      render: (_, record) => getStatutTag(record),
    },
    {
      title: 'Interactions',
      key: 'interactions',
      render: (_, record) => {
        const likes = likesData[`publication_${record.id}`];
        const comments = commentsData[`publication_${record.id}`] || 0;
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ color: '#52c41a' }}>
              <LikeOutlined /> {likes?.likeCount || 0}
            </span>
            <span style={{ color: '#ff4d4f' }}>
              <DislikeOutlined /> {likes?.dislikeCount || 0}
            </span>
            <span style={{ color: '#1890ff' }}>
              <CommentOutlined /> {comments}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Tooltip title="Voir la publication">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => window.open(`/publication/${record.id}`, '_blank')}
            />
          </Tooltip>
          {!record.actif && !record.rejete && (
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => window.open(`/publication/modifier/${record.id}`, '_blank')}
              />
            </Tooltip>
          )}
          {record.rejete && (
            <Tooltip title="Voir motif de rejet">
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                style={{ color: '#ff4d4f' }}
                onClick={() => showRejectModal(record, 'publication')}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Chargement de votre tableau de bord...</p>
      </div>
    );
  }

  return (
    <div>
      <Header />

      {/* Hero Section */}
      <div
        className="inner-hero bg-cover"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/img/bg/inner-hero-bg.jpg)`,
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="main-heading">
                <h1>Tableau de Bord</h1>
                <p>Gérez vos projets et publications en un clin d'œil</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="sp">
        <div className="container">

          {/* Profil utilisateur */}
          <div className="row mb-4">
            <div className="col-lg-12">
              <Card className="profile-card" style={{ background: 'linear-gradient(135deg, #FF8C00 0%, #FFD700 100%)', color: 'white' }}>
                <div className="d-flex align-items-center">
                  <Avatar
                    size={80}
                    src={
                      currentUser && currentUser.photo
                        ? baseApiUrl.defaults.baseURL + `/image/${currentUser.photo}`
                        : "https://via.placeholder.com/80"
                    }
                    icon={!currentUser?.photo && <TrophyOutlined />}
                  />
                  <div className="ms-3">
                    <h2 className="mb-1" style={{ color: 'white' }}>
                      Bienvenue, {currentUser?.prenom} {currentUser?.nom}
                    </h2>
                    <p className="mb-0" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Voici un aperçu de vos activités
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Statistiques principales */}
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Projets"
                  value={statistiques.totalProjets}
                  prefix={<ProjectOutlined style={{ color: '#FA8C16' }} />}
                  valueStyle={{ color: '#FA8C16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Publications"
                  value={statistiques.totalPublications}
                  prefix={<FileTextOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Éléments Validés"
                  value={statistiques.elementsValides}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Éléments Rejetés"
                  value={statistiques.elementsRejetes}
                  prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Progress des validations */}
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} lg={12}>
              <Card title="Progression des Projets" className="progress-card">
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Projets Validés</span>
                    <span>{statistiques.projetsValides}/{statistiques.totalProjets}</span>
                  </div>
                  <Progress
                    percent={statistiques.totalProjets > 0 ? Math.round((statistiques.projetsValides / statistiques.totalProjets) * 100) : 0}
                    strokeColor="#52c41a"
                  />
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>En Attente</span>
                    <span>{statistiques.projetsEnAttente}</span>
                  </div>
                  <Progress
                    percent={statistiques.totalProjets > 0 ? Math.round((statistiques.projetsEnAttente / statistiques.totalProjets) * 100) : 0}
                    strokeColor="#FA8C16"
                  />
                </div>
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Rejetés</span>
                    <span>{statistiques.projetsRejetes}</span>
                  </div>
                  <Progress
                    percent={statistiques.totalProjets > 0 ? Math.round((statistiques.projetsRejetes / statistiques.totalProjets) * 100) : 0}
                    strokeColor="#ff4d4f"
                  />
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Progression des Publications" className="progress-card">
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Publications Validées</span>
                    <span>{statistiques.publicationsValides}/{statistiques.totalPublications}</span>
                  </div>
                  <Progress
                    percent={statistiques.totalPublications > 0 ? Math.round((statistiques.publicationsValides / statistiques.totalPublications) * 100) : 0}
                    strokeColor="#52c41a"
                  />
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>En Attente</span>
                    <span>{statistiques.publicationsEnAttente}</span>
                  </div>
                  <Progress
                    percent={statistiques.totalPublications > 0 ? Math.round((statistiques.publicationsEnAttente / statistiques.totalPublications) * 100) : 0}
                    strokeColor="#FA8C16"
                  />
                </div>
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Rejetées</span>
                    <span>{statistiques.publicationsRejetees}</span>
                  </div>
                  <Progress
                    percent={statistiques.totalPublications > 0 ? Math.round((statistiques.publicationsRejetees / statistiques.totalPublications) * 100) : 0}
                    strokeColor="#ff4d4f"
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Activités récentes */}
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} lg={12}>
              <Card
                title="Activités Récentes"
                extra={<CalendarOutlined />}
                className="activities-card"
              >
                {recentActivities.length > 0 ? (
                  <Timeline>
                    {recentActivities.map((activite, index) => (
                      <Timeline.Item
                        key={index}
                        dot={
                          activite.type === 'projet'
                            ? <ProjectOutlined style={{ color: '#FA8C16' }} />
                            : <FileTextOutlined style={{ color: '#52c41a' }} />
                        }
                        color={activite.rejete ? 'red' : activite.statut ? 'green' : 'orange'}
                      >
                        <div>
                          <p className="mb-1">
                            <strong>{activite.titre}</strong>
                          </p>
                          <p className="mb-1 text-muted">
                            {activite.type === 'projet' ? 'Projet' : 'Publication'} - {formatDate(activite.date)}
                          </p>
                          <Badge
                            status={activite.rejete ? 'error' : activite.statut ? 'success' : 'warning'}
                            text={activite.rejete ? 'Rejeté' : activite.statut ? 'Validé' : 'En attente'}
                          />
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                ) : (
                  <Empty
                    description="Aucune activité récente"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Card>
            </Col>

            {/* Actions rapides */}
            <Col xs={24} lg={12}>
              <Card title="Actions Rapides" className="quick-actions-card">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Link to="/projet/ajouter">
                      <Button
                        type="primary"
                        size="large"
                        icon={<PlusOutlined />}
                        block
                        style={{ height: '60px', backgroundColor: '#FA8C16', borderColor: '#FA8C16' }}
                      >
                        Nouveau Projet
                      </Button>
                    </Link>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Link to="/publication/ajouter">
                      <Button
                        type="primary"
                        size="large"
                        icon={<PlusOutlined />}
                        block
                        style={{ height: '60px', backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Nouvelle Publication
                      </Button>
                    </Link>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Link to="/mesprojets">
                      <Button
                        size="large"
                        icon={<ProjectOutlined />}
                        block
                        style={{ height: '60px' }}
                      >
                        Mes Projets
                      </Button>
                    </Link>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Link to="/mespublications">
                      <Button
                        size="large"
                        icon={<FileTextOutlined />}
                        block
                        style={{ height: '60px' }}
                      >
                        Mes Publications
                      </Button>
                    </Link>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Tabs pour Projets et Publications */}
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24}>
              <Card>
                <Tabs defaultActiveKey="projets">
                  <TabPane tab="Mes Projets Récents" key="projets">
                    {projets.length > 0 ? (
                      <Table
                        columns={projetColumns}
                        dataSource={projets.slice(0, 5)}
                        pagination={false}
                        rowKey="id"
                        size="small"
                      />
                    ) : (
                      <Empty
                        description="Aucun projet trouvé"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Link to="/projet/ajouter">
                          <Button type="primary" icon={<PlusOutlined />} style={{ backgroundColor: '#FA8C16', borderColor: '#FA8C16' }}>
                            Créer votre premier projet
                          </Button>
                        </Link>
                      </Empty>
                    )}
                  </TabPane>

                  <TabPane tab="Mes Publications Récentes" key="publications">
                    {publications.length > 0 ? (
                      <Table
                        columns={publicationColumns}
                        dataSource={publications.slice(0, 5)}
                        pagination={false}
                        rowKey="id"
                        size="small"
                      />
                    ) : (
                      <Empty
                        description="Aucune publication trouvée"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Link to="/publication/ajouter">
                          <Button type="primary" icon={<PlusOutlined />} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                            Créer votre première publication
                          </Button>
                        </Link>
                      </Empty>
                    )}
                  </TabPane>
                </Tabs>
              </Card>
            </Col>
          </Row>

          {/* Alertes et notifications */}
          {(statistiques.projetsEnAttente > 0 || statistiques.publicationsEnAttente > 0 || statistiques.elementsRejetes > 0) && (
            <Row gutter={[16, 16]} className="mb-4">
              <Col xs={24}>
                <Card className="alert-card">
                  <div className="d-flex align-items-center mb-3">
                    <ExclamationCircleOutlined
                      style={{ color: '#FA8C16', fontSize: '24px', marginRight: '16px' }}
                    />
                    <div>
                      <h4 className="mb-1">État de vos contenus</h4>
                    </div>
                  </div>

                  {(statistiques.projetsEnAttente > 0 || statistiques.publicationsEnAttente > 0) && (
                    <div className="mb-2">
                      <Badge status="warning" />
                      <span className="ms-2">
                        Vous avez {statistiques.projetsEnAttente} projet(s) et {statistiques.publicationsEnAttente} publication(s)
                        en attente de validation par l'administration.
                      </span>
                    </div>
                  )}

                  {statistiques.elementsRejetes > 0 && (
                    <div>
                      <Badge status="error" />
                      <span className="ms-2">
                        {statistiques.elementsRejetes} élément(s) ont été rejetés.
                        Cliquez sur l'icône d'information pour voir les motifs de rejet.
                      </span>
                    </div>)}
                </Card>
              </Col>
            </Row>
          )}

        </div>
      </div>

      <Footer1 />
      <Footer />
    </div>
  );
}

export default UserDashboard;
